import { DateTime } from 'luxon';

import { AvailabilityStatus, IUnavailability } from './types';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

type AvailabilityFilterParams = {
  user_id?: number;
  date?: Date;
  start_date?: Date;
  until_date?: Date;
};

const toUtcDate = (value: Date | string | null | undefined): DateTime | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return DateTime.fromJSDate(value, { zone: 'utc' });
  }
  const parsed = DateTime.fromISO(value, { zone: 'utc' });
  return parsed.isValid ? parsed : null;
};

export const filterUnavailabilities = (
  unavailabilities: IUnavailability[],
  filters: AvailabilityFilterParams,
): IUnavailability[] => {
  const date = filters.date ? (toUtcDate(filters.date)?.startOf('day') ?? null) : null;
  const startDate = (filters.start_date ? toUtcDate(filters.start_date) : date)?.startOf('day') ?? null;
  const untilDate = (filters.until_date ? toUtcDate(filters.until_date) : date)?.startOf('day') ?? null;

  return unavailabilities.filter((entry) => {
    if (entry.deleted_at) {
      return false;
    }
    if (filters.user_id && entry.user_id !== filters.user_id) {
      return false;
    }

    const entryStart = toUtcDate(entry.start_date)?.startOf('day') ?? null;
    const entryUntil = toUtcDate(entry.until_date)?.startOf('day') ?? null;

    if (untilDate && entryStart && untilDate < entryStart) {
      return false;
    }
    if (startDate && entryUntil && startDate > entryUntil) {
      return false;
    }
    if (entry.reoccurring && date) {
      const dayKey = DAY_KEYS[date.weekday % 7];
      return !!entry[dayKey];
    }

    return true;
  });
};

export const availabilityStatus = (
  unavailabilities: IUnavailability[],
  filters: AvailabilityFilterParams,
): AvailabilityStatus => {
  const filtered = filterUnavailabilities(unavailabilities, filters);
  return filtered.length > 0 ? 'none' : 'full';
};

export const dailyResultsForUsers = (
  filters: { date?: Date },
  users: Array<{ id: number }>,
  unavailabilities: IUnavailability[],
): Record<number, IUnavailability[]> => {
  const results: Record<number, IUnavailability[]> = {};

  users.forEach((user) => {
    results[user.id] = filterUnavailabilities(unavailabilities, {
      user_id: user.id,
      date: filters.date,
    });
  });

  return results;
};
